import { BaseAxios } from 'src/config/axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { publicApiPath } from 'src/common/publicApiPath';
import { useStatusOfVerificationProcessQuery } from 'src/common/components/RequireCorrectStep/useProcessStatusQuery';
import { useVerificationId } from 'src/common/hooks/useVerificationId';
import { useFrontendTypeParam } from 'src/common/hooks/useFrontendType';
import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { VerificationStatus } from 'src/common/models/verificationModels';
import { verificationQueryKeys } from 'src/common/verificationQueryKeys';
import { environment } from 'src/config/environment';
import { FlowType } from '../../common/models/processModels';
import {
  frontendTypeParam,
  SmartFlowPageRoute,
  verificationIdParam,
} from 'src/routes';

enum VerificationEntryPoint {
  RedirectUrl = 'REDIRECT_URL',
  WebSdk = 'WEB_SDK',
}

const startProcess = ({
  verificationId,
  verificationEntryPoint,
}: {
  verificationId?: string;
  verificationEntryPoint: VerificationEntryPoint;
}) =>
  BaseAxios.post(
    `${publicApiPath}/process/${verificationId}/start?verificationEntryPoint=${verificationEntryPoint}`
  );

export const useStartProcess = () => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const location = useLocation();

  const verificationId = useVerificationId();
  const frontType = useFrontendTypeParam();
  const status = useStatusOfVerificationProcessQuery(verificationId);

  const { mutate } = useMutation((verificationId?: string) =>
    startProcess({
      verificationId,
      verificationEntryPoint: environment.isWebSdk
        ? VerificationEntryPoint.WebSdk
        : VerificationEntryPoint.RedirectUrl,
    })
  );

  useEffect(() => {
    if (
      verificationId &&
      status.data?.verificationStatus === VerificationStatus.Initiated
    ) {
      mutate(verificationId, {
        onSuccess: () => {
          queryClient.setQueryData(
            verificationQueryKeys.status(verificationId),
            VerificationStatus.Started
          );
        },
      });
    }

    if (verificationId && status.data?.verificationFlow === FlowType.SmarFlow) {
      if (location.pathname !== SmartFlowPageRoute) {
        history.replace({
          pathname: SmartFlowPageRoute,
          search: frontType
            ? `?${verificationIdParam}=${verificationId}&${frontendTypeParam}=${frontType}`
            : `?${verificationIdParam}=${verificationId}`,
        });
      }
    }
  }, [verificationId, status.data, location.pathname]);
};
